import { useEffect, useId, useRef, useState } from 'react'
import { useOutsideClick } from '../hooks/useOutsideClick'
import { capabilityMatches } from '../capabilitySearch'

// Searchable single-select for one capability. Type to filter by name, label or
// description; pick with a click or ↑/↓ + Enter; Esc closes the list. Options
// are device capabilities ({ capability_id, name, label, description }).
// Typing after a pick clears it, so the input never shows a stale selection.
export default function CapabilityCombobox({ options, value, onChange, onPicked, autoFocus = false }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const ref = useOutsideClick(() => setOpen(false), open)
  const listRef = useRef(null)
  const listId = useId()

  const selected = options.find(o => o.capability_id === value) ?? null
  const matches = options.filter(o => capabilityMatches(o, query))

  // Keep the highlighted option visible while arrowing through a long list.
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function pick(option) {
    onChange(option.capability_id)
    setQuery('')
    setOpen(false)
    onPicked?.()
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) { setOpen(true); setActive(0); return }
      setActive(i => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (open && matches[active]) {
        e.preventDefault()
        pick(matches[active])
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault()
        setOpen(false)
      }
    }
  }

  const optionId = i => `${listId}-opt-${i}`

  return (
    <div className="relative flex-1" ref={ref}>
      <input
        type="text"
        className="input input-sm w-full"
        role="combobox"
        aria-label="Capability to constrain"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? optionId(active) : undefined}
        placeholder="Search capabilities…"
        autoFocus={autoFocus}
        value={selected && !open ? `${selected.name} — ${selected.label}` : query}
        onFocus={() => { setOpen(true); setActive(0) }}
        onClick={() => setOpen(true)}
        onChange={e => {
          if (value) onChange('')
          setQuery(e.target.value)
          setActive(0)
          setOpen(true)
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul
          id={listId}
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 mt-1 z-20 menu flex-nowrap bg-base-100 border border-base-300 rounded-box shadow-lg max-h-72 overflow-y-auto p-1"
        >
          {matches.length === 0 ? (
            <li className="opacity-40 text-xs px-3 py-2">No matching capabilities</li>
          ) : (
            matches.map((o, i) => (
              <li key={o.capability_id}>
                <div
                  id={optionId(i)}
                  data-index={i}
                  role="option"
                  aria-selected={o.capability_id === value}
                  className={`flex flex-col items-start gap-0 py-1.5 ${i === active ? 'menu-active' : ''}`}
                  // mousedown, not click: fires before the input blurs.
                  onMouseDown={e => { e.preventDefault(); pick(o) }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <span>{o.label}</span>
                    <span className="font-mono text-xs opacity-60">{o.name}</span>
                  </span>
                  {o.description && <span className="text-xs opacity-50 line-clamp-1">{o.description}</span>}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
